import { DisconnectReason, Room, RoomEvent } from 'livekit-client';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AgentRegistry } from './agents';
import { LeftScreen } from './components/LeftScreen';
import { Lobby, type JoinChoices, type JoinError } from './components/Lobby';
import { MeetingRoom } from './components/MeetingRoom';
import { APP_CONFIG } from './config';
import { currentEmbedContext, isEmbedded } from './embed';
import { CATALOGS, uiLanguageFor } from './i18n';
import { StringsContext } from './i18n/context';
import { isLanguageCode, languageFromPreferences, type LanguageCode } from './languages';
import { readPref, writePref } from './prefs';
import { homePath, parseRoute, roomPath, type Route } from './routing';
import { TokenRequestError, requestToken } from './token';

type Stage =
  | { readonly kind: 'lobby' }
  | {
      readonly kind: 'room';
      readonly room: Room;
      readonly agents: AgentRegistry;
      readonly code: string;
      readonly language: LanguageCode;
    }
  | { readonly kind: 'left'; readonly unexpected: boolean };

function currentRoute(): Route {
  return parseRoute(window.location.pathname, window.location.search, APP_CONFIG.basePath);
}

function initialLanguage(): LanguageCode {
  const stored = readPref('language');
  if (isLanguageCode(stored)) return stored;
  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language];
  return languageFromPreferences(preferred.filter(Boolean));
}

export function App() {
  const [route, setRoute] = useState<Route>(currentRoute);
  const [language, setLanguage] = useState<LanguageCode>(initialLanguage);
  const [stage, setStage] = useState<Stage>({ kind: 'lobby' });
  const embedded = useMemo(() => isEmbedded(currentEmbedContext()), []);

  const uiLanguage = uiLanguageFor(language);
  const strings = CATALOGS[uiLanguage];

  useEffect(() => {
    document.documentElement.lang = uiLanguage;
    document.title = strings.appTitle;
  }, [uiLanguage, strings]);

  useEffect(() => {
    const onPopState = () => {
      setRoute(currentRoute());
      setStage((current) => (current.kind === 'room' ? current : { kind: 'lobby' }));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const join = useCallback(
    async (choices: JoinChoices): Promise<JoinError | null> => {
      const { devices } = choices;
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        audioCaptureDefaults: {
          echoCancellation: true, // the first echo guard: the translated voice must not loop back
          noiseSuppression: true,
          autoGainControl: true,
          ...(devices.microphoneId ? { deviceId: devices.microphoneId } : {}),
        },
        videoCaptureDefaults: devices.cameraId ? { deviceId: devices.cameraId } : {},
      });
      // Autoplay rules: unlock audio playback now, inside the click, before anything awaits.
      room.startAudio().catch(() => undefined);
      // Watches for the interpreter from before the connection exists.
      const agents = new AgentRegistry(room);

      writePref('name', choices.name);
      writePref('language', choices.language);
      try {
        const details = await requestToken(APP_CONFIG.tokenUrl, {
          ...(route.code ? { room: route.code } : {}),
          name: choices.name,
          lang: choices.language,
          tenant: APP_CONFIG.tenant,
        });
        await room.connect(details.serverUrl, details.participantToken);
        if (route.code !== details.room) {
          window.history.replaceState(null, '', `${roomPath(details.room, APP_CONFIG.basePath)}${window.location.hash}`);
          setRoute({ code: details.room, invalidCode: false });
        }
        room.once(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
          setStage({ kind: 'left', unexpected: reason !== DisconnectReason.CLIENT_INITIATED });
        });
        setStage({ kind: 'room', room, agents, code: details.room, language: choices.language });
        // A refused camera or microphone leaves the person in the room; the toggles show it.
        await Promise.allSettled([
          room.localParticipant.setMicrophoneEnabled(devices.microphoneOn),
          room.localParticipant.setCameraEnabled(devices.cameraOn),
        ]);
        return null;
      } catch (error) {
        await room.disconnect().catch(() => undefined);
        return error instanceof TokenRequestError && error.status === 429 ? 'rateLimited' : 'connect';
      }
    },
    [route.code],
  );

  const rejoin = useCallback(() => setStage({ kind: 'lobby' }), []);
  const newMeeting = useCallback(() => {
    window.history.pushState(null, '', homePath(APP_CONFIG.basePath));
    setRoute({ code: null, invalidCode: false });
    setStage({ kind: 'lobby' });
  }, []);

  return (
    <StringsContext.Provider value={strings}>
      <div className={embedded ? 'app app--embedded' : 'app'} data-lk-theme="default">
        {stage.kind === 'room' ? (
          <MeetingRoom
            room={stage.room}
            agents={stage.agents}
            code={stage.code}
            language={stage.language}
            embedded={embedded}
          />
        ) : stage.kind === 'left' ? (
          <LeftScreen unexpected={stage.unexpected} onRejoin={rejoin} onNewMeeting={newMeeting} />
        ) : (
          <Lobby route={route} language={language} onLanguageChange={setLanguage} onJoin={join} embedded={embedded} />
        )}
      </div>
    </StringsContext.Provider>
  );
}
